import React, { useMemo, useState } from 'react';
import { FiBookOpen, FiLock, FiMic } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useEntitlements } from '../entitlements/useEntitlements';
import { useAudioPlayer } from '../audio/AudioPlayerContext';
import { generateAudioStream } from '../api/audioApi';
import { DepthLevel, GenerateAudioPayload, LengthLevel } from '../types';
import {
  DEFAULT_PODCAST_SPEAKERS,
  evaluateGate,
  FREE_PODCAST_DEPTHS,
  GenFormat,
  GenOptions,
  TIER_RANK,
} from '../entitlements/gating';
import { AUDIO_CREATED_EVENT } from '../hooks/useLibrary';
import UpgradeModal from './UpgradeModal';
import '../pages/CreatePage.css';

const PODCAST_DEPTHS: { value: DepthLevel; label: string; hint: string }[] = [
  { value: 'brief', label: 'Brief', hint: '~5–7 min' },
  { value: 'moderate', label: 'Standard', hint: '~15–20 min' },
  { value: 'comprehensive', label: 'Deep', hint: '~30–35 min' },
  { value: 'exhaustive', label: 'Exhaustive', hint: '~45–60 min' },
];

const AUDIOBOOK_LENGTHS: { value: LengthLevel; label: string; hint: string }[] = [
  { value: 'brief', label: 'Brief', hint: '~7–15 min' },
  { value: 'moderate', label: 'Standard', hint: '~30–50 min' },
  { value: 'comprehensive', label: 'Deep', hint: '~35–60 min' },
  { value: 'full', label: 'Full', hint: '~50–90 min' },
];

const TONES = ['Default', 'Conversational', 'Energetic', 'Calm', 'Academic'];
const SPEAKER_OPTIONS = [2, 3, 4];

interface Props {
  documentId: number;
  // Fires after a successful generation so the audio list can refresh.
  onCreated?: () => void;
}

// Podcast/audiobook generation form bound to a single source document. Reuses
// the entitlement gating + streaming generation flow from the standalone
// Create page, minus the multi-document picker and the text-based Direct Read.
export default function CreateAudioPanel({ documentId, onCreated }: Props) {
  const navigate = useNavigate();
  const { tier } = useEntitlements();
  const player = useAudioPlayer();

  const [format, setFormat] = useState<Exclude<GenFormat, 'tts'>>('podcast');
  const [depth, setDepth] = useState<DepthLevel>('moderate');
  const [length, setLength] = useState<LengthLevel>('moderate');
  const [numSpeakers, setNumSpeakers] = useState(DEFAULT_PODCAST_SPEAKERS);
  const [tone, setTone] = useState('Default');
  const [title, setTitle] = useState('');
  const [focusPrompt, setFocusPrompt] = useState('');

  const [generating, setGenerating] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<{ label: string; requiredTier: 'basic' | 'pro' } | null>(null);

  const options = useMemo<GenOptions>(() => {
    if (format === 'audiobook') return { format, lengthLevel: length, tone: tone !== 'Default' ? tone : undefined };
    return { format, depthLevel: depth, numSpeakers, tone: tone !== 'Default' ? tone : undefined };
  }, [format, depth, length, numSpeakers, tone]);

  const gate = useMemo(() => evaluateGate(options, tier), [options, tier]);

  const depthLocked = (d: DepthLevel) => tier === 'free' && !FREE_PODCAST_DEPTHS.includes(d);
  const speakersLocked = (n: number) => tier === 'free' && n > DEFAULT_PODCAST_SPEAKERS;
  const toneLocked = tier === 'free';
  const audiobookLocked = TIER_RANK[tier] < TIER_RANK.pro;

  const openUpgrade = () => {
    const required = gate.requiredTier === 'pro' ? 'pro' : 'basic';
    const label = gate.locks[0]?.label ?? 'This feature';
    setUpgrade({ label, requiredTier: required });
  };

  const generate = async () => {
    setError(null);
    setDone(null);
    setStage(null);
    if (!gate.allowed) {
      openUpgrade();
      return;
    }
    setGenerating(true);
    setStage('Starting generation');
    try {
      const payload: GenerateAudioPayload = {
        document_ids: [documentId],
        format,
        title: title.trim() || undefined,
        custom_search_prompt: focusPrompt.trim() || undefined,
        tone: tone !== 'Default' ? tone.toLowerCase() : undefined,
      };
      if (format === 'podcast') {
        payload.depth_level = depth;
        payload.num_speakers = numSpeakers;
      } else {
        payload.length_level = length;
      }

      const result = await generateAudioStream(payload, (s) => setStage(s));
      window.dispatchEvent(new CustomEvent(AUDIO_CREATED_EVENT, { detail: { format } }));
      onCreated?.();

      if (result.document_id) {
        await player.play({
          id: result.audio_content_id ?? result.document_id,
          title: result.title || title.trim() || 'Generated audio',
          documentId: result.document_id,
          format,
          durationSeconds: result.duration_seconds,
        });
      }
      setDone(`Your ${format} is ready and now playing.`);
      setTitle('');
      setFocusPrompt('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed.');
    } finally {
      setGenerating(false);
      setStage(null);
    }
  };

  return (
    <div className="create-main card">
      <div className="fmt-tabs">
        <button className={`fmt-tab${format === 'podcast' ? ' fmt-tab--active' : ''}`} onClick={() => setFormat('podcast')}>
          <FiMic /> Podcast <span className="fmt-tab__free">Free</span>
        </button>
        <button className={`fmt-tab${format === 'audiobook' ? ' fmt-tab--active' : ''}`} onClick={() => setFormat('audiobook')}>
          <FiBookOpen /> Audiobook {audiobookLocked && <FiLock className="fmt-tab__lock" />}
        </button>
      </div>

      <div className="field create-field">
        <label className="field__label">{format === 'podcast' ? 'Length & depth' : 'Length'}</label>
        <div className="seg">
          {(format === 'podcast' ? PODCAST_DEPTHS : AUDIOBOOK_LENGTHS).map((opt) => {
            const locked = format === 'podcast' ? depthLocked(opt.value as DepthLevel) : audiobookLocked;
            const active = format === 'podcast' ? depth === opt.value : length === opt.value;
            return (
              <button
                key={opt.value}
                className={`seg__btn${active ? ' seg__btn--active' : ''}`}
                onClick={() =>
                  format === 'podcast' ? setDepth(opt.value as DepthLevel) : setLength(opt.value as LengthLevel)
                }
              >
                <span className="seg__label">
                  {opt.label} {locked && <FiLock className="seg__lock" />}
                </span>
                <span className="seg__hint">{opt.hint}</span>
              </button>
            );
          })}
        </div>
      </div>

      {format === 'podcast' && (
        <div className="field create-field">
          <label className="field__label">
            Voices <span className="muted">(hosts)</span>
          </label>
          <div className="seg seg--compact">
            {SPEAKER_OPTIONS.map((n) => (
              <button
                key={n}
                className={`seg__btn${numSpeakers === n ? ' seg__btn--active' : ''}`}
                onClick={() => setNumSpeakers(n)}
              >
                <span className="seg__label">
                  {n} {speakersLocked(n) && <FiLock className="seg__lock" />}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="field create-field">
        <label className="field__label" htmlFor="panel-tone">
          Tone {toneLocked && <FiLock className="inline-lock" />}
        </label>
        <select id="panel-tone" className="select" value={tone} onChange={(e) => setTone(e.target.value)}>
          {TONES.map((t) => (
            <option key={t} value={t}>
              {t}
              {t !== 'Default' ? ' (premium)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="field create-field">
        <label className="field__label" htmlFor="panel-title">
          Title <span className="muted">(optional)</span>
        </label>
        <input
          id="panel-title"
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Chapter 5 Deep Dive"
        />
      </div>

      <div className="field create-field">
        <label className="field__label" htmlFor="panel-focus">
          Focus prompt <span className="muted">(optional)</span>
        </label>
        <textarea
          id="panel-focus"
          className="textarea"
          value={focusPrompt}
          onChange={(e) => setFocusPrompt(e.target.value)}
          placeholder="e.g. Focus on the key theories and give real-world examples"
        />
      </div>

      {error && <div className="create-error">{error}</div>}
      {done && (
        <div className="create-done">
          {done} <button className="link-btn" onClick={() => navigate('/library')}>View library</button>
        </div>
      )}

      <button className="btn btn--primary btn--block btn--lg create-generate" onClick={generate} disabled={generating}>
        {generating ? (
          <>
            <span className="spinner spinner--sm" /> {stage || 'Generating…'}
          </>
        ) : !gate.allowed ? (
          <>
            <FiLock /> Unlock &amp; generate
          </>
        ) : (
          <>
            {format === 'audiobook' ? <FiBookOpen /> : <FiMic />} Generate {format}
          </>
        )}
      </button>

      {generating && (
        <div className="gen-overlay">
          <div className="gen-overlay__card">
            <span className="spinner" />
            <p className="gen-overlay__stage">{stage || 'Working…'}</p>
            <p className="muted">This can take 1–3 minutes. Keep this tab open.</p>
          </div>
        </div>
      )}

      <UpgradeModal
        open={Boolean(upgrade)}
        onClose={() => setUpgrade(null)}
        requiredTier={upgrade?.requiredTier ?? null}
        featureLabel={upgrade?.label ?? 'This feature'}
        onSubscribe={() => {
          setUpgrade(null);
          navigate('/account');
        }}
      />
    </div>
  );
}
